import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/authStore';
import { api } from '../../lib/api';

interface MobileProfileSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function MobileProfileSheet({ isOpen, onClose }: MobileProfileSheetProps) {
  const { t } = useTranslation('mobile');
  const { user, checkAuth } = useAuthStore();
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user?.displayName) {
      setDisplayName(user.displayName);
    }
  }, [user]);

  // Reset state when opening
  useEffect(() => {
    if (isOpen) {
      setError(null);
      setShowSuccess(false);
      setDisplayName(user?.displayName || '');
    }
  }, [isOpen, user?.displayName]);

  const handleSave = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.patch('/api/auth/me', {
        displayName: displayName.trim() || null,
      });

      if (response.ok) {
        setShowSuccess(true);
        await checkAuth();
        setTimeout(() => {
          setShowSuccess(false);
          onClose();
        }, 1500);
      } else {
        const data = await response.json() as { error?: string };
        setError(data.error || t('profile.updateFailed'));
      }
    } catch {
      setError(t('profile.connectionError'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/50 animate-fadeIn" onClick={onClose} />
      <div className="absolute bottom-0 left-0 right-0 bg-white dark:bg-gray-900 rounded-t-3xl shadow-2xl animate-slideUp safe-area-inset-bottom">
        <div className="flex justify-center py-3">
          <div className="w-12 h-1.5 bg-gray-300 dark:bg-gray-600 rounded-full" />
        </div>

        <div className="px-4 pb-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('profile.title')}</h2>
            <button
              onClick={handleSave}
              disabled={loading}
              className="px-4 py-2 bg-primary-600 text-white rounded-xl font-medium text-sm active:scale-95 transition-transform disabled:opacity-50"
            >
              {loading ? t('common.loading') : t('common.save')}
            </button>
          </div>

          {/* Success Message */}
          {showSuccess && (
            <div className="mb-4 flex items-center gap-2 px-4 py-3 bg-green-100 dark:bg-green-900/30 rounded-xl text-green-700 dark:text-green-400">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="font-medium">{t('profile.updateSuccess')}</span>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-4 flex items-center gap-2 px-4 py-3 bg-red-100 dark:bg-red-900/30 rounded-xl text-red-700 dark:text-red-400">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span className="font-medium">{error}</span>
            </div>
          )}

          {/* Avatar */}
          <div className="flex justify-center mb-6">
            <div className="w-24 h-24 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
              <span className="text-4xl font-bold text-primary-600 dark:text-primary-400">
                {displayName?.charAt(0) || user?.email?.charAt(0) || 'U'}
              </span>
            </div>
          </div>

          {/* Form Fields */}
          <div className="space-y-4">
            {/* Display Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('profile.displayName')}
              </label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder={t('profile.displayNamePlaceholder')}
                className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-800 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {t('profile.displayNameHint')}
              </p>
            </div>

            {/* Email (read-only) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('profile.email')}
              </label>
              <div className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-800 rounded-xl text-gray-500 dark:text-gray-400">
                {user?.email}
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {t('profile.emailHint')}
              </p>
            </div>

            {/* Role (read-only) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('profile.role')}
              </label>
              <div className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-800 rounded-xl text-gray-500 dark:text-gray-400 capitalize">
                {user?.role || 'user'}
              </div>
            </div>

            {/* Account Created (read-only) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('profile.accountCreated')}
              </label>
              <div className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-800 rounded-xl text-gray-500 dark:text-gray-400">
                {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : '-'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
